package dev.fritz2.components

import dev.fritz2.components.compat.Input
import dev.fritz2.components.compat.div
import dev.fritz2.components.compat.input
import dev.fritz2.components.compat.label
import dev.fritz2.components.foundations.Component
import dev.fritz2.components.foundations.ComponentProperty
import dev.fritz2.components.foundations.DynamicComponentProperty
import dev.fritz2.components.foundations.ElementMixin
import dev.fritz2.components.foundations.ElementProperties
import dev.fritz2.components.foundations.EventMixin
import dev.fritz2.components.foundations.EventProperties
import dev.fritz2.components.foundations.InputFormMixin
import dev.fritz2.components.foundations.InputFormProperties
import dev.fritz2.core.HtmlTag
import dev.fritz2.core.RenderContext
import dev.fritz2.core.Store
import dev.fritz2.core.checked
import dev.fritz2.core.disabled
import dev.fritz2.core.`for`
import dev.fritz2.core.name
import dev.fritz2.core.readOnly
import dev.fritz2.core.states
import dev.fritz2.core.type
import dev.fritz2.core.value
import dev.fritz2.styling.StyleClass
import dev.fritz2.styling.params.BasicParams
import dev.fritz2.styling.params.BoxParams
import dev.fritz2.styling.params.Style
import dev.fritz2.styling.style
import dev.fritz2.styling.theme.FormSizesStyles
import dev.fritz2.styling.theme.Theme
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLLabelElement


/**
 * This component generates a *single* radio.
 * So this component supports the use case to select or deselect an option.
 * In general prefer a [checkbox] for this use case.
 * If an application has a strong focus on mobile, consider a [switch] instead!
 *
 * This component mostly exist only as base item for a [radioGroup]!
 *
 * You can set different kind of properties like the label or different styling aspects like the colors of the
 * background, the label or the checked state. Further more there are configuration functions for accessing the checked
 * state of this box or totally disable it.
 *
 * Example usage
 * ```
 * val cheeseStore = storeOf(false)
 * radio(value = cheeseStore) {
 *      label("with extra cheese") // set the label
 * }
 * ```
 *
 * @see RadioComponent
 *
 * @param styling a lambda expression for declaring the styling as fritz2's styling DSL
 * @param value a boolean store to handle the state and its changes automatically
 * @param baseClass optional CSS class that should be applied to the element
 * @param id the ID of the element
 * @param prefix the prefix for the generated CSS class resulting in the form ``$prefix-$hash``
 * @param build a lambda expression for setting up the component itself. Details in [RadioComponent]
 */
fun RenderContext.radio(
    styling: BasicParams.() -> Unit = {},
    value: Store<Boolean>? = null,
    baseClass: StyleClass = StyleClass.None,
    id: String? = value?.id,
    prefix: String = "radioComponent",
    build: RadioComponent.() -> Unit = {}
): HtmlTag<HTMLLabelElement> = RadioComponent(value).apply(build).render(this, styling, baseClass, id, prefix)

/**
 * This class combines the _configuration_ and the core styling of a radio button.
 *
 * This class offers the following _configuration_ features:
 *  - the label(mapping) static or dynamic via a [Flow<String>] or customized content see the examples below
 *  - some predefined styling variants (size)
 *  - the style of the selected state
 *  - the style of the radio
 *  - the style of the label
 *  - the groupname
 *  - link an external boolean flow to set the selected state of the box
 *  - link an external boolean flow to set the disabled state of the box
 *  - link events of the switch like ``changes`` with external handlers
 *
 *  This can be done within a functional expression that is the last parameter of the factory function, called
 *  `build`. It offers an initialized instance of this [RadioComponent] class as receiver, so every mutating
 *  method can be called for configuring the desired state for rendering the radio.
 *
 * Example usage
 * ```
 * // simple use case
 * val cheeseStore = storeOf(false)
 * radio(value = cheeseStore) {
 *      label("with extra cheese") // set the label
 * }
 *
 * // manual event handling and styling options
 * radio {
 *      label("with extra cheese") // set the label
 *      size { normal } // choose a predefined size
 *      selected(cheeseStore.data) // link a [Flow<Boolean>] in order to visualize the checked state
 *      events { // open inner context with all DOM-element events
 *          changes.states() handledBy cheeseStore.update // connect the changes event with the state store
 *      }
 *      element {
 *          // exposes the underlying HTML input element for direct access. Use with caution!
 *      }
 * }
 * ```
 */
open class RadioComponent(protected val value: Store<Boolean>? = null) :
    Component<HtmlTag<HTMLLabelElement>>,
    EventProperties<HTMLInputElement> by EventMixin(),
    ElementProperties<Input> by ElementMixin(),
    InputFormProperties by InputFormMixin() {

    private val radioInputStaticCss = style(
        """
            position: absolute;
            border: 0px;
            clip: rect(0px, 0px, 0px, 0px);
            height: 0px;
            width: 0px;
            overflow: hidden;
            white-space: nowrap;
            &:focus{
                outline: none;
            }
            &:focus + label::before {
                box-shadow: 0 0 1px ${Theme().colors.gray700};
            }
            &:disabled + label {
                color: ${Theme().colors.disabled};
                cursor: not-allowed;
            }
            &:disabled + label::before {
                opacity: 0.3;
                cursor: not-allowed;
                boxShadow: none;
                color: ${Theme().colors.disabled};
            }
        """.trimIndent(),
        prefix = "radioInput"
    )

    val size = ComponentProperty<FormSizesStyles.() -> Style<BasicParams>> { Theme().radio.sizes.normal }

    private var label: (HtmlTag<HTMLDivElement>.() -> Unit)? = null
    fun label(value: String) {
        label = {
            +value
        }
    }

    fun label(value: Flow<String>) {
        label = {
            value.renderText()
        }
    }

    fun label(value: (HtmlTag<HTMLDivElement>.() -> Unit)) {
        label = value
    }

    val labelStyle = ComponentProperty(Theme().radio.label)
    val selectedStyle = ComponentProperty(Theme().radio.selected)
    val selected = DynamicComponentProperty(flowOf(false))
    val groupName = ComponentProperty<String?>(null)

    override fun render(
        context: RenderContext,
        styling: BoxParams.() -> Unit,
        baseClass: StyleClass,
        id: String?,
        prefix: String
    ): HtmlTag<HTMLLabelElement> {
        return with(context) {
            label({
                display { inlineFlex }
                alignItems { center }
                verticalAlign { top }
                css("-webkit-box-align: center;")
                this@RadioComponent.size.value.invoke(Theme().radio.sizes)()
                // to "capture" the invisible, absolute positioned `input`, see `radioInputStaticCss`
                position { relative { } }
            }, baseClass, prefix = prefix) {
                if (id != null) `for`(id)
                input({
                    Theme().radio.input()
                    children("&[checked] + div") {
                        this@RadioComponent.selectedStyle.value()
                    }
                }, this@RadioComponent.radioInputStaticCss, id, prefix) {
                    disabled(this@RadioComponent.disabled.values)
                    readOnly(this@RadioComponent.readonly.values)
                    type("radio")
                    this@RadioComponent.groupName.value?.let { name(it) }
                    checked(this@RadioComponent.value?.data ?: this@RadioComponent.selected.values)
                    value("X")
                    this@RadioComponent.value?.let { changes.states() handledBy it.update }
                    this@RadioComponent.events.value.invoke(this)
                    this@RadioComponent.element.value.invoke(this)
                }

                div({
                    Theme().radio.default()
                    styling()
                }) { }

                this@RadioComponent.label?.let {
                    div({
                        this@RadioComponent.labelStyle.value()
                    }) {
                        it(this)
                    }
                }
            }
        }
    }
}
